import React, { useEffect, useRef, useState } from 'react';
import pluralize from 'pluralize';
import {
  useGetBulkSetpointLimitsLazyQuery,
  useValidateBulkConfigThermostatsChangesMutation,
  useCommitBulkConfigThermostatsBatchMutation,
  BulkThermostatCompatibility,
  BulkConfigThermostatsInput,
  BulkConfigValidationResult,
  GetBulkSetpointLimitsQuery,
} from '../../../../types/generated-types';
import SelectProperty from '../common/select-property';
import SelectUnitTypes from './select-unit-types';
import SelectBuildings from './select-buildings';
import SelectFloors from './select-floors';
import SelectCUGroups from './select-cu-groups';
import ConfigureTStats from './configure-tstats';
import BulkVerify from '../common/bulk-verify';
import BulkCommit from '../common/bulk-commit';
import {
  TStatConfig,
  TStatFilters,
  UnitFilters,
  BulkThermostatsStep,
  Property,
  Building,
  Floor,
  Unit,
  initialTStatConfig,
  initialTStatFilters,
  initialCommitProgress,
} from '../types';
import { convertSetpoints } from '../utils/bulk-utils';
import { Celsius } from '../../../system/models/temperatureUnits';
import { useBulkUpdateEntities } from '../../../system/EntityManager';

// Top level container for thermostat bulk configuration. Contains the majority
// of state and logic shared between the various steps
export default function BulkThermostats() {
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null,
  );

  const [step, setBulkThermostatsStep] = useState(
    BulkThermostatsStep.SelectProperty,
  );
  const [tStatConfig, setTStatConfig] = useState(initialTStatConfig);
  const [tStatFilters, setTStatFilters] = useState(initialTStatFilters);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
  const [selectedFloors, setSelectedFloors] = useState<string[]>([]);
  const [selectedCUGroups, setSelectedCUGroups] = useState<string[]>([]);
  const [commitProgress, setCommitProgress] = useState(initialCommitProgress);
  const [error, setError] = useState('');
  const [capabilityDevices, setCapabilityDevices] = useState<
    BulkThermostatCompatibility[]
  >([]);
  const [excludedDeviceIds, setExcludedDeviceIds] = useState<string[]>([]);
  const [setpointLimits, setSetpointLimits] =
    useState<GetBulkSetpointLimitsQuery['getBulkSetpointLimits']>();
  const [discoveryPending, setDiscoveryPending] = useState(false);
  const [acceptedPreview, setAcceptedPreview] = useState<{
    input: BulkConfigThermostatsInput;
    result: BulkConfigValidationResult;
  }>();
  const [previewPending, setPreviewPending] = useState(false);
  const discoveryGeneration = useRef(0);
  const previewGeneration = useRef(0);
  const configRevision = useRef(0);

  const {
    properties,
    units,
    buildings,
    floors,
    devices,
    loading: bulkEntitiesLoading,
    error: bulkEntitiesError,
  } = useBulkUpdateEntities(selectedPropertyId);

  // Combined units filters object - easier to share between child components and functions
  const unitFilters: UnitFilters = {
    selectedUnitTypes,
    selectedBuildings,
    selectedFloors,
    selectedCUGroups,
  };

  // Format current state for use in graphql operations
  function formatBulkThermostatInput(): BulkConfigThermostatsInput {
    return {
      propertyId: selectedPropertyId,
      unitTypes: selectedUnitTypes,
      buildingIds: selectedBuildings,
      floorIds: selectedFloors,
      cuGroups: selectedCUGroups,
      config: {
        ...tStatConfig,
        // ensure setpoints are sent to the server as Celsius
        setpoints: convertSetpoints(tStatConfig.setpoints, Celsius),
      },
      filters: tStatFilters,
    };
  }

  const [loadSetpointLimits] = useGetBulkSetpointLimitsLazyQuery();

  const [validateChanges] = useValidateBulkConfigThermostatsChangesMutation({
    variables: {
      input: formatBulkThermostatInput(),
    },
  });

  const [commitConfig] = useCommitBulkConfigThermostatsBatchMutation();

  const selectionKey = JSON.stringify([
    selectedPropertyId,
    selectedUnitTypes,
    selectedBuildings,
    selectedFloors,
    selectedCUGroups,
    tStatFilters,
  ]);
  // Discovery reads the current input but does not restart for ordinary edits.
  // Keep its response separate from the exact input/result accepted at Verify.
  const discoveryRequest = useRef({
    input: formatBulkThermostatInput(),
    loadSetpointLimits,
    validateChanges,
  });
  discoveryRequest.current = {
    input: formatBulkThermostatInput(),
    loadSetpointLimits,
    validateChanges,
  };
  const currentSelectionKey = useRef(selectionKey);
  currentSelectionKey.current = selectionKey;
  useEffect(() => {
    if (step !== BulkThermostatsStep.ConfigureTStats) return;
    const generation = ++discoveryGeneration.current;
    const revision = configRevision.current;
    const request = discoveryRequest.current;
    const isCurrent = () => generation === discoveryGeneration.current;
    setDiscoveryPending(true);
    setCapabilityDevices([]);
    setSetpointLimits(undefined);
    (async () => {
      try {
        const limits = await request.loadSetpointLimits({
          variables: { input: request.input },
        });
        if (!isCurrent()) return;
        if (limits.error) throw new Error(limits.error.message);
        setSetpointLimits(limits.data?.getBulkSetpointLimits);
        const preview = await request.validateChanges({
          variables: { input: request.input },
        });
        if (!isCurrent()) return;
        if (preview.errors) throw new Error(preview.errors[0].message);
        const rows =
          preview.data?.validateBulkConfigThermostatsChanges.devices ?? [];
        setCapabilityDevices(rows);
        if (
          revision === 0 &&
          configRevision.current === 0 &&
          rows.length &&
          rows.every((row) => row.autoMode === 'single')
        )
          setTStatConfig((prev) => ({
            ...prev,
            setpoints: { ...prev.setpoints, autoMode: 'single' },
          }));
      } catch (error) {
        if (isCurrent()) setError('Error loading bulk thermostat capabilities');
      } finally {
        if (isCurrent()) setDiscoveryPending(false);
      }
    })();
    return () => {
      if (isCurrent()) discoveryGeneration.current = generation + 1;
    };
  }, [step, selectionKey]);

  function invalidatePreview() {
    ++previewGeneration.current;
    setAcceptedPreview(undefined);
    setPreviewPending(false);
    setExcludedDeviceIds([]);
    setCommitProgress(initialCommitProgress);
  }

  function handleSelectProperty(propertyId: string) {
    ++discoveryGeneration.current;
    invalidatePreview();
    // Clear all filters when a new property is selected
    setSelectedPropertyId(propertyId);
    setSelectedProperty(
      properties.find((prop) => prop._id === propertyId) ?? null,
    );
    setError('');
    setCapabilityDevices([]);
    setExcludedDeviceIds([]);
    setSelectedUnitTypes([]);
    setSelectedBuildings([]);
    setSelectedFloors([]);
    setSelectedCUGroups([]);
  }

  function handleSelectUnitType(unitType: string, checked: boolean) {
    // Add or remove the given unit type
    const newUnitTypes = checked
      ? [...selectedUnitTypes, unitType]
      : selectedUnitTypes.filter((t) => t !== unitType);
    setSelectedUnitTypes(newUnitTypes);
    // Reset all filters that depend on selected unit types
    setSelectedBuildings([]);
    setSelectedFloors([]);
  }

  function handleSelectBuilding(buildingId: string, checked: boolean) {
    // Add or remove the given building
    const newBuildings = checked
      ? [...selectedBuildings, buildingId]
      : selectedBuildings.filter((b) => b !== buildingId);
    setSelectedBuildings(newBuildings);
    // Reset all filters that depend on selected buildings
    setSelectedFloors([]);
  }

  function handleSelectFloor(floorId: string, checked: boolean) {
    // Add or remove the given floor
    const newFloors = checked
      ? [...selectedFloors, floorId]
      : selectedFloors.filter((f) => f !== floorId);
    setSelectedFloors(newFloors);
  }

  function handleSelectCUGroup(cuGroup: string, checked: boolean) {
    // Add or remove the given CU group
    const newCUGroups = checked
      ? [...selectedCUGroups, cuGroup]
      : selectedCUGroups.filter((t) => t !== cuGroup);
    setSelectedCUGroups(newCUGroups);
  }

  function toggleUnitTypes(unitTypes: string[]) {
    if (unitTypes.length > selectedUnitTypes.length) {
      setSelectedUnitTypes(unitTypes);
    } else {
      setSelectedUnitTypes([]);
    }
    // Reset all filters that depend on selected unit types
    setSelectedBuildings([]);
    setSelectedFloors([]);
  }

  function toggleBuildings(buildings: string[]) {
    if (buildings.length > selectedBuildings.length) {
      setSelectedBuildings(buildings);
    } else {
      setSelectedBuildings([]);
    }
    // Reset all filters that depend on selected buildings
    setSelectedFloors([]);
  }

  function toggleFloors(floors: string[]) {
    if (floors.length > selectedFloors.length) {
      setSelectedFloors(floors);
    } else {
      setSelectedFloors([]);
    }
  }

  function toggleCUGroups(cuGroups: string[]) {
    if (cuGroups.length > selectedCUGroups.length) {
      setSelectedCUGroups(cuGroups);
    } else {
      setSelectedCUGroups([]);
    }
  }

  function handleTStatConfigChange(changes: Partial<TStatConfig>) {
    ++configRevision.current;
    invalidatePreview();
    setTStatConfig({
      ...tStatConfig,
      ...changes,
    });
  }

  function handleTStatFilterChange(changes: Partial<TStatFilters>) {
    ++discoveryGeneration.current;
    invalidatePreview();
    setTStatFilters({
      ...tStatFilters,
      ...changes,
    });
  }

  function handleNext() {
    // Navigate to the next step, if the selections on the current step are valid
    if (validateCurrentBulkThermostatsStep()) {
      const steps = getBulkThermostatsStepsForProperty();
      const currentIndex = steps.indexOf(step);
      const nextBulkThermostatsStep = steps[currentIndex + 1];
      // Kick off any operations to perform on the next step, then navigate to it.
      // No need to wait for operations to finish
      executeOperationsForBulkThermostatsStep(nextBulkThermostatsStep)
        .then(() => {
          console.log('Done bulk update step');
        })
        .catch((e) => {
          console.log(`Error while executing bulk update step: ${e.message}`);
        });
      setBulkThermostatsStep(nextBulkThermostatsStep);
    }
  }

  function handleBack() {
    ++discoveryGeneration.current;
    invalidatePreview();
    // Navigate to the previous step
    setError('');
    const steps = getBulkThermostatsStepsForProperty();
    const currentIndex = steps.indexOf(step);
    const prevBulkThermostatsStep = steps[currentIndex - 1];
    setBulkThermostatsStep(prevBulkThermostatsStep);
  }

  function handleRestart() {
    // Reset state and navigate back to the first step
    handleSelectProperty('');
    configRevision.current = 0;
    setTStatConfig(initialTStatConfig);
    setTStatFilters(initialTStatFilters);
    setCommitProgress(initialCommitProgress);
    setError('');
    setBulkThermostatsStep(BulkThermostatsStep.SelectProperty);
  }

  // Verify that the selections made on the current step are valid, and that the user
  // is able to proceed to the next step. If not, display an appropriate error message.
  function validateCurrentBulkThermostatsStep(): boolean {
    let errMsg = '';
    switch (step) {
      case BulkThermostatsStep.SelectProperty:
        if (!selectedPropertyId) {
          errMsg = 'Must select a property to configure';
        } else if (bulkEntitiesLoading) {
          errMsg = 'Loading property data. Please try again in a moment.';
        } else if (bulkEntitiesError) {
          errMsg = 'Error loading property data.';
        }
        break;
      case BulkThermostatsStep.SelectUnitTypes:
        if (selectedUnitTypes.length === 0) {
          errMsg = 'Must select at least one unit type';
        }
        break;
      case BulkThermostatsStep.SelectBuildings:
        if (selectedBuildings.length === 0) {
          errMsg = 'Must select at least one building';
        }
        break;
      case BulkThermostatsStep.SelectFloors:
        if (selectedFloors.length === 0) {
          errMsg = 'Must select at least one ' + getFloorLabel(false);
        }
        break;
      case BulkThermostatsStep.SelectCUGroups:
        if (selectedCUGroups.length === 0) {
          errMsg = 'Must select at least one CU group';
        }
        break;
      case BulkThermostatsStep.ConfigureTStats:
        if (discoveryPending || capabilityDevices.length === 0)
          errMsg =
            'Thermostat capabilities are still loading or unavailable. Please try again.';
        break;
      case BulkThermostatsStep.BulkVerify:
        if (previewPending || !acceptedPreview) {
          errMsg =
            'Wait for the current configuration preview before committing';
        } else if (
          acceptedPreview.result.devices?.some(
            (device) =>
              !device.compatible &&
              !excludedDeviceIds.includes(device.deviceId),
          )
        ) {
          errMsg =
            'Explicitly exclude every incompatible thermostat before committing';
        } else if (
          commitProgress.deviceIds.filter(
            (id) => !excludedDeviceIds.includes(id),
          ).length === 0
        ) {
          errMsg = 'No thermostats to configure';
        }
        break;
    }
    setError(errMsg);
    return !errMsg;
  }

  // Perform any operations that should be triggered on proceeding to the given step
  async function executeOperationsForBulkThermostatsStep(
    step: BulkThermostatsStep,
  ) {
    switch (step) {
      case BulkThermostatsStep.BulkVerify:
        const generation = ++previewGeneration.current;
        const input = formatBulkThermostatInput();
        const requestSelection = selectionKey;
        const isCurrent = () =>
          generation === previewGeneration.current &&
          requestSelection === currentSelectionKey.current;
        try {
          setAcceptedPreview(undefined);
          setPreviewPending(true);
          setExcludedDeviceIds([]);
          setCommitProgress(initialCommitProgress);
          // Validate the current thermostat changes, and determine how many devices require configuring
          const result = await validateChanges({
            variables: { input },
          });
          if (!isCurrent()) return;
          if (result.errors) {
            throw new Error(result.errors[0].message);
          } else if (result.data) {
            setAcceptedPreview({
              input,
              result: result.data.validateBulkConfigThermostatsChanges,
            });
            setCommitProgress((prevProgress) => {
              return {
                ...prevProgress,
                deviceIds:
                  result.data?.validateBulkConfigThermostatsChanges.deviceIds ||
                  [],
              };
            });
          }
        } catch (error) {
          if (isCurrent())
            setError('Error validating thermostat configuration');
        } finally {
          if (isCurrent()) setPreviewPending(false);
        }
        break;
      case BulkThermostatsStep.BulkCommit:
        try {
          if (!acceptedPreview)
            throw new Error(
              'Reload the configuration preview before committing',
            );
          const deviceIds = commitProgress.deviceIds.filter(
            (id) => !excludedDeviceIds.includes(id),
          );
          setCommitProgress((prev) => ({ ...prev, deviceIds }));
          const result = await commitConfig({
            variables: {
              input: acceptedPreview.input,
              deviceIds,
              excludedDeviceIds,
              previewStates:
                acceptedPreview.result.devices
                  ?.filter((row) => deviceIds.includes(row.deviceId))
                  .map((row) => row.previewState) ?? [],
            },
          });
          if (result.errors) throw new Error(result.errors[0].message);
          const outcomes = result.data?.commitBulkConfigThermostatsBatch;
          if (!outcomes) throw new Error('No batch outcome received');
          setCommitProgress((prev) => ({
            ...prev,
            numConfigured: outcomes.filter((row) => row.success).length,
            numFailed: outcomes.filter((row) => !row.success).length,
          }));
          const failures = outcomes.filter((row) => !row.success);
          if (failures.length)
            setError(
              failures.map((row) => `${row.deviceId}: ${row.error}`).join('; '),
            );
        } catch (error) {
          setError(
            error instanceof Error ? error.message : 'Batch preflight failed',
          );
          setCommitProgress((prev) => ({ ...prev, terminated: true }));
        }
        break;
    }
  }

  // Get filtered list of units that include a thermostat
  function getUnitsWithThermostats(): Unit[] {
    const tStatUnitIds = devices.map((d) => d.unitId) || [];
    return units.filter((u) => tStatUnitIds.includes(u?._id || '')) || [];
  }

  // Get list of selectable units types, filtered to only include units with thermostats
  function getSelectableUnitTypes(): string[] {
    const tStatUnitIds = getUnitsWithThermostats();
    const unitTypes = tStatUnitIds.map((u) => u?.type || '');
    return Array.from(new Set(unitTypes));
  }

  // Get filtered list of units, based on the selected unit types
  function filterUnitsBySelectedUnitTypes(): Unit[] {
    return (
      units.filter((u) => {
        if (selectedUnitTypes.length > 0) {
          return selectedUnitTypes.includes(u?.type || '');
        }
        return true;
      }) || []
    );
  }

  // Get list of selectable buildings, filtered base on selected unit types
  function getSelectableBuildings(): Building[] {
    const units = filterUnitsBySelectedUnitTypes();
    const buildingIdMap: { [id: string]: boolean } = {};
    units.forEach((u) => {
      if (u?.buildingId) {
        buildingIdMap[u.buildingId] = true;
      }
    });
    const buildingIds = Object.keys(buildingIdMap);
    return buildings.filter((b) => buildingIds.includes(b._id));
  }

  function getSelectedBuildings(): Building[] {
    const buildings = getSelectableBuildings();
    if (selectedBuildings.length > 0) {
      return buildings.filter((b) => selectedBuildings.includes(b?._id || ''));
    }
    return buildings;
  }

  // Get list of selectable floors, filtered base on selected unit types and buildings
  function getSelectableFloors(): Floor[] {
    const buildingIds = getSelectedBuildings()
      .map((b) => b?._id || '')
      .filter((id) => !!id);
    return floors.filter((f) => buildingIds.includes(f?.buildingId || ''));
  }

  function getSelectableCUGroups(): string[] {
    return [
      ...units.reduce((res, unit) => {
        if (unit?.cuid) {
          res.add(unit.cuid);
        }
        return res;
      }, new Set<string>()),
    ];
  }

  function propertyHasMultipleUnitTypes() {
    return getSelectableUnitTypes().length > 1;
  }

  function propertyHasMultipleBuildings() {
    return getSelectableBuildings().length > 1;
  }

  function propertyHasMultipleFloors() {
    return getSelectableFloors().length > 1;
  }

  function propertyHasCUGroups() {
    return getSelectableCUGroups().length > 0;
  }

  function getFloorLabel(plural: boolean) {
    const label = selectedProperty?.floorLabel || 'Floor';
    return pluralize(label, plural ? 2 : 1);
  }

  // Ordered list of bulk config steps, based on the selected property
  function getBulkThermostatsStepsForProperty(): BulkThermostatsStep[] {
    const steps = [BulkThermostatsStep.SelectProperty];
    if (propertyHasCUGroups()) {
      // If selectedProperty has CU groups, only allow filtering based on those CU groups,
      // ignore any unit types, buildings, and floors
      steps.push(BulkThermostatsStep.SelectCUGroups);
    } else {
      if (propertyHasMultipleUnitTypes()) {
        steps.push(BulkThermostatsStep.SelectUnitTypes);
      }
      if (propertyHasMultipleBuildings()) {
        steps.push(BulkThermostatsStep.SelectBuildings);
      }
      if (propertyHasMultipleFloors()) {
        steps.push(BulkThermostatsStep.SelectFloors);
      }
    }
    steps.push(BulkThermostatsStep.ConfigureTStats);
    steps.push(BulkThermostatsStep.BulkVerify);
    steps.push(BulkThermostatsStep.BulkCommit);
    return steps;
  }

  if (!selectedProperty || step === BulkThermostatsStep.SelectProperty) {
    return (
      <SelectProperty
        properties={properties}
        selectedPropertyId={selectedPropertyId}
        error={error}
        onSelectProperty={handleSelectProperty}
        onNext={handleNext}
      />
    );
  }

  switch (step) {
    case BulkThermostatsStep.SelectUnitTypes:
      return (
        <SelectUnitTypes
          unitTypes={getSelectableUnitTypes()}
          property={selectedProperty}
          unitFilters={unitFilters}
          error={error}
          onSelectUnitType={handleSelectUnitType}
          toggleUnitTypes={toggleUnitTypes}
          onBack={handleBack}
          onNext={handleNext}
        />
      );
    case BulkThermostatsStep.SelectBuildings:
      return (
        <SelectBuildings
          buildings={getSelectableBuildings()}
          unitFilters={unitFilters}
          error={error}
          onSelectBuilding={handleSelectBuilding}
          toggleBuildings={toggleBuildings}
          onBack={handleBack}
          onNext={handleNext}
        />
      );
    case BulkThermostatsStep.SelectFloors:
      return (
        <SelectFloors
          floors={getSelectableFloors()}
          floorLabel={getFloorLabel(true)}
          buildings={getSelectedBuildings()}
          unitFilters={unitFilters}
          error={error}
          onSelectFloor={handleSelectFloor}
          toggleFloors={toggleFloors}
          onBack={handleBack}
          onNext={handleNext}
        />
      );
    case BulkThermostatsStep.SelectCUGroups:
      return (
        <SelectCUGroups
          cuGroups={getSelectableCUGroups()}
          units={getUnitsWithThermostats()}
          unitFilters={unitFilters}
          error={error}
          onSelectCUGroup={handleSelectCUGroup}
          toggleCUGroups={toggleCUGroups}
          onBack={handleBack}
          onNext={handleNext}
        />
      );
    case BulkThermostatsStep.ConfigureTStats:
      return (
        <ConfigureTStats
          autoModes={capabilityDevices.map((row) => row.autoMode)}
          nativeDevices={capabilityDevices.some(
            (row) => row.sourceType === 'Conductor',
          )}
          setpointLimits={setpointLimits}
          tStatConfig={tStatConfig}
          tStatFilters={tStatFilters}
          error={error}
          onTStatConfigChange={handleTStatConfigChange}
          onTStatFilterChange={handleTStatFilterChange}
          onBack={handleBack}
          onNext={handleNext}
          onCancel={handleRestart}
        />
      );
    case BulkThermostatsStep.BulkVerify:
      return (
        <BulkVerify
          validationResult={acceptedPreview?.result}
          error={error}
          onBack={handleBack}
          onNext={handleNext}
          onCancel={handleRestart}
          excludedDeviceIds={excludedDeviceIds}
          onExcludeDevice={(deviceId, excluded) =>
            setExcludedDeviceIds((prev) =>
              excluded
                ? [...prev, deviceId]
                : prev.filter((id) => id !== deviceId),
            )
          }
        />
      );
    case BulkThermostatsStep.BulkCommit:
      return (
        <BulkCommit
          commitProgress={commitProgress}
          error={error}
          onTerminate={() => undefined}
          allowTerminate={false}
          onRestart={handleRestart}
        />
      );
  }
}
